import { environment } from '@doltech/core/lib/environment/environment';
import { usePDFService } from '@doltech/core/lib/pdf/pdfService';
import { IconEmojiObjectsInboxTray } from '@doltech/icons-from-dev/emoji/objects/IconEmojiObjectsInboxTray';
import { IconFileTypePdfDocument } from '@doltech/icons-from-dev/fileType/IconFileTypePdfDocument';
import { IconArrowsPushDown } from '@doltech/icons/IconArrowsPushDown';
import { usePdfTrackingParams } from '@doltech/utils/lib/pdf';
import { mergeUrlWithParams } from '@doltech/utils/lib/url';
import { Spin } from 'antd';
import * as React from 'react';
import { useInView } from 'react-cool-inview';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../../hooks/useDeviceQuery.hook';
import { RequiredAuthFake } from '../../../Auth/RequiredAuthFake';
import { colorsV2 } from '../../../colors-v2';
import { Box } from '../../../Common/Box';
import { Typography } from '../../../DesignSystemV2/Typography';

const Main = styled.div`
  margin-top: 40px;
  @media ${deviceBreakpoints.mobile} {
    margin-top: 16px;
  }

  .section-header {
    margin-bottom: 16px;

    @media ${deviceBreakpoints.mobile} {
      margin-bottom: 0;
      padding: 0 16px 8px;
    }

    &-title {
      display: flex;
      align-items: center;

      span {
        margin-left: 8px;

        @media ${deviceBreakpoints.mobile} {
          margin-left: 4px;
        }
      }
    }
  }
  a {
    color: inherit;
  }
  .download-container {
    border-radius: 16px;
    .download-description {
      margin-bottom: 16px;
    }
    .download-info {
      padding: 16px 24px;
      display: flex;
      align-items: center;
      background-color: ${colorsV2.gray10};
      border-radius: 16px;
      cursor: pointer;
      .icon-file-type {
        padding: 10px;
        font-size: 28px;
        margin-right: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .file-info {
        flex: 1;
        overflow: hidden;

        .file-info-title {
          margin-bottom: 4px;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
        .file-info-uploaded-date {
          color: ${colorsV2.black70};
        }
      }
      .icon-download {
        margin-left: 18px;
        min-width: 48px;
        width: 48px;
        height: 48px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 21.6px;
        background-color: ${colorsV2.gray40};
        border-radius: 8px;
      }
    }
  }
`;

const PfdContainer = styled.div`
  opacity: 1;
  pointer-events: none;
  position: fixed;
`;

interface DownloadPDFProps {
  id?: string;
  testName: string;
  fileName: string;
  homepageUrl: string;
  children: any;
  isVocab?: boolean;
}

export const DownloadPDF = (props: DownloadPDFProps) => {
  const { id, testName, fileName, children, homepageUrl, isVocab = false } = props;
  const [downloading, setDownloading] = React.useState(false);

  const ref = React.useRef();
  const pdfService = usePDFService();
  const pdfTrackingParams = usePdfTrackingParams();
  const { observe, inView } = useInView({
    // Stop observe when the target enters the viewport, so the "inView" only triggered once
    unobserveOnEnter: true,
    // For better UX, we can grow the root margin so the image will be loaded before it comes to the viewport
    rootMargin: '50px',
  });

  const onDownloadPdf = () => {
    setDownloading(true);
    pdfService
      .renderPDF(
        {
          html: ref.current,
        },
        fileName,
        {
          header: {
            url: mergeUrlWithParams(homepageUrl, pdfTrackingParams),
          },
          footer: {
            url: mergeUrlWithParams(
              environment.REACT_APP_DOL_CORP_WEBSITE_HOST_NAME,
              pdfTrackingParams
            ),
          },
        }
      )
      .finally(() => setDownloading(false));
  };

  return (
    <RequiredAuthFake
      title="Tài liệu này chỉ dành riêng cho thành viên của dolacademy.vn"
      onLoginSuccess={onDownloadPdf}
    >
      {({ forceLoginToProceedAction }) => {
        return (
          <Main id={id} onClick={() => forceLoginToProceedAction(onDownloadPdf)} ref={observe}>
            <Spin spinning={downloading}>
              <div className="section-header">
                <Typography.Title
                  level={2}
                  className="section-header-title"
                  variant="semi-bold/24"
                  tabletVariant="semi-bold/32"
                >
                  <IconEmojiObjectsInboxTray />
                  <span>Download PDF</span>
                </Typography.Title>
              </div>
              <Box
                padding="24px 0 0 0"
                background={colorsV2.white100}
                className="download-container"
              >
                <div className="download-description">
                  <Typography.Text variant="semi-bold/16">
                    {isVocab
                      ? `Bạn có thể tải bản đẹp của từ vựng ${testName} tại đây`
                      : `Bạn có thể tải bản đẹp của đề và đáp án ${testName} tại đây`}
                  </Typography.Text>
                </div>
                <div>
                  <div role="button" aria-hidden="true" className="download-info">
                    <div className="icon-file-type">
                      <IconFileTypePdfDocument />
                    </div>
                    <div className="file-info">
                      <div className="file-info-title">
                        <Typography.Text variant="semi-bold/16">{fileName}</Typography.Text>
                      </div>
                    </div>
                    <div className="icon-download">
                      <IconArrowsPushDown />
                    </div>
                  </div>
                </div>
              </Box>
              <PfdContainer className="pdf-container">
                {inView && <div ref={ref}>{children}</div>}
              </PfdContainer>
            </Spin>
          </Main>
        );
      }}
    </RequiredAuthFake>
  );
};
